/**
 * @file vuexstore for states used by tag inputs in the active draft composer modal (popover anchor, new section input, ...)
 */
import { vSet } from 'bits/vue'
import type { ActiveInputTag, PopoverAnchor } from 'types'
import { Module } from 'vuex'

interface ComposerModalTagInputsState {
  sectionChipPosition: PopoverAnchor
  locationChipPosition: PopoverAnchor
  postColorChipPosition: PopoverAnchor
  xNewSectionPrompt: boolean
  activeInputTag: ActiveInputTag
}

const initialState = (): ComposerModalTagInputsState => ({
  sectionChipPosition: { top: 0, left: 0, width: 0 },
  locationChipPosition: { top: 0, left: 0, width: 0 },
  postColorChipPosition: { top: 0, left: 0, width: 0 },
  xNewSectionPrompt: false,
  activeInputTag: 'none',
})

const ComposerModalTagInputsModule: Module<ComposerModalTagInputsState, object> = {
  namespaced: true,
  state: initialState,
  getters: {
    isShowingTagMenu: (state): boolean => state.activeInputTag !== 'none',
    xSectionMenu: (state): boolean => state.activeInputTag === 'section',
    xLocationMenu: (state): boolean => state.activeInputTag === 'location',
    xPostColorMenu: (state): boolean => state.activeInputTag === 'postColor',
    xNewSectionPrompt: (state): boolean => state.xNewSectionPrompt,
    sectionChipPosition: (state): PopoverAnchor => state.sectionChipPosition,
    locationChipPosition: (state): PopoverAnchor => state.locationChipPosition,
    postColorChipPosition: (state): PopoverAnchor => state.postColorChipPosition,
  },
  mutations: {
    OPEN_POPOVER(state, { inputType }: { inputType: ActiveInputTag }): void {
      state.activeInputTag = inputType
    },
    CLOSE_POPOVER(state): void {
      state.activeInputTag = 'none'
    },
    UPDATE_POPOVER_ANCHOR_POSITIONS(
      state,
      {
        sectionChipPosition,
        locationChipPosition,
        postColorChipPosition,
      }: {
        sectionChipPosition: PopoverAnchor
        locationChipPosition: PopoverAnchor
        postColorChipPosition: PopoverAnchor
      },
    ): void {
      vSet(state, 'sectionChipPosition', {
        ...sectionChipPosition,
      })
      vSet(state, 'locationChipPosition', {
        ...locationChipPosition,
      })
      vSet(state, 'postColorChipPosition', {
        ...postColorChipPosition,
      })
    },
    OPEN_NEW_SECTION_PROMPT(state): void {
      state.xNewSectionPrompt = true
    },
    CLOSE_NEW_SECTION_PROMPT(state): void {
      state.xNewSectionPrompt = false
    },
  },
  actions: {
    openPopover({ commit, getters }, { inputType }: { inputType: ActiveInputTag }): void {
      commit('OPEN_POPOVER', { inputType })
    },
    closePopover({ commit }): void {
      commit('CLOSE_POPOVER')
    },
    updatePopoverAnchorPositions(
      { commit },
      {
        sectionChipPosition,
        locationChipPosition,
        postColorChipPosition,
      }: {
        sectionChipPosition: PopoverAnchor
        locationChipPosition: PopoverAnchor
        postColorChipPosition: PopoverAnchor
      },
    ): void {
      commit('UPDATE_POPOVER_ANCHOR_POSITIONS', {
        sectionChipPosition,
        locationChipPosition,
        postColorChipPosition,
      })
    },
    openNewSectionPrompt({ commit }): void {
      commit('OPEN_NEW_SECTION_PROMPT')
    },
    closeNewSectionPrompt({ commit }): void {
      commit('CLOSE_NEW_SECTION_PROMPT')
    },
  },
}

export default ComposerModalTagInputsModule
export { ComposerModalTagInputsState }
